import { useEffect } from 'react';
import { useBackupPendingCount } from '@/hooks/useBackupPending';
import { useMachineAlertsCount } from '@/hooks/useMachineAlerts';
import { usePermissions } from '@/hooks/usePermissions';
import { useUnreadCount } from '@/hooks/useNotifiche';
import { usePreventiviDaInviareCount } from '@/hooks/usePreventivi';
import { useTickets } from '@/hooks/useTickets';
import type { SidebarBadgeKey } from '@/navigation/sidebarNav';

export type SidebarBadgeCounts = Partial<Record<SidebarBadgeKey, number>>;

function positiveCount(value: number | undefined, ready: boolean, failed: boolean): number | undefined {
  if (!ready || failed) return undefined;
  if (value == null || value <= 0) return undefined;
  return value;
}

/**
 * Contatori badge della sidebar.
 *
 * Riusa le query già usate dalla chrome più la coda preventivi mensili.
 * In loading o in errore il badge sparisce.
 */
export function useSidebarBadges(): SidebarBadgeCounts {
  const { canBrowseTickets, canManagePreventivi, isTenantUser } = usePermissions();
  const openTicketsQuery = useTickets({
    stato: isTenantUser ? ['aperto', 'in_lavorazione'] : ['aperto'],
    pageSize: 1,
    enabled: canBrowseTickets || isTenantUser,
  });
  const backupQuery = useBackupPendingCount();
  const alertsQuery = useMachineAlertsCount();
  const unreadQuery = useUnreadCount();
  const mensiliQuery = usePreventiviDaInviareCount(canManagePreventivi);

  useEffect(() => {
    if (backupQuery.isError) {
      console.warn('[sidebar] Badge «Da mappare» non disponibile.', backupQuery.error);
    }
  }, [backupQuery.isError, backupQuery.error]);

  useEffect(() => {
    if (alertsQuery.isError) {
      console.warn('[sidebar] Badge «Alert» non disponibile.', alertsQuery.error);
    }
  }, [alertsQuery.isError, alertsQuery.error]);

  useEffect(() => {
    if (unreadQuery.isError) {
      console.warn('[sidebar] Badge notifiche non letto non disponibile.', unreadQuery.error);
    }
  }, [unreadQuery.isError, unreadQuery.error]);

  useEffect(() => {
    if (openTicketsQuery.isError) {
      console.warn('[sidebar] Badge ticket aperti non disponibile.', openTicketsQuery.error);
    }
  }, [openTicketsQuery.isError, openTicketsQuery.error]);

  return {
    openTickets: positiveCount(openTicketsQuery.data?.meta.total, openTicketsQuery.isSuccess, openTicketsQuery.isError),
    backupPending: positiveCount(backupQuery.data?.count, backupQuery.isSuccess, backupQuery.isError),
    machineAlerts: positiveCount(alertsQuery.data, alertsQuery.isSuccess, alertsQuery.isError),
    unreadNotifications: positiveCount(unreadQuery.data?.count, unreadQuery.isSuccess, unreadQuery.isError),
    preventiviDaInviare: positiveCount(mensiliQuery.data?.count, mensiliQuery.isSuccess, mensiliQuery.isError),
  };
}
